import { Box, FlexLayout, H4, Icon } from '@deity/falcon-ui';
import type { FC, ReactElement } from 'react';
import { Children, cloneElement, isValidElement, useEffect, useState } from 'react';

type DropDownProps = {
  placeholder: string;
  defaultValue?: string | number | undefined;
  defaultDisplayName?: string | number;
  defaultIcon?: string;
  withIcon?: boolean;
  disabled?: boolean;
  children: JSX.Element | JSX.Element[];
  onChange: (val) => void;
};
export const DropDown: FC<DropDownProps> = ({
  placeholder,
  defaultValue,
  defaultDisplayName,
  withIcon,
  defaultIcon,
  disabled,
  children,
  onChange
}) => {
  const [selectedVal, setSelectedVal] = useState<string | number | undefined>(defaultValue);
  const [iconName, setIconName] = useState<string | undefined>();
  const [name, setName] = useState<string | number | undefined>();
  const [fold, setFold] = useState(true);
  const handleChange = (val, icon: string, displayName: string) => {
    setSelectedVal(val);
    setIconName(icon);
    setName(displayName);
    setFold(true);
  };

  useEffect(() => {
    if (selectedVal !== defaultValue) {
      onChange(selectedVal);
    }
  }, [selectedVal]);

  useEffect(() => {
    setIconName(defaultIcon);
    setName(defaultDisplayName);
    setSelectedVal(defaultValue);
  }, [defaultValue, defaultDisplayName, defaultIcon]);

  const dropdownItemsWithProps = Children.map(children, child => {
    if (isValidElement(child)) {
      return cloneElement(child as ReactElement<any>, { handleChange });
    }

    return child;
  });

  return (
    <Box style={{ position: 'relative', flexGrow: 1 }} tabIndex={0} onBlur={() => setFold(true)}>
      <FlexLayout
        bg="white"
        p="xs"
        justifyContent="space-between"
        alignItems="center"
        border="regular"
        borderColor="border"
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        onClick={() => !disabled && setFold(!fold)}
        css={{
          height: '48px',
          ':hover': {
            backgroundColor: disabled ? 'white' : '#f3f5f7'
          }
        }}
      >
        <FlexLayout alignItems="center">
          {iconName && withIcon && (
            <Icon fill="badgeText" stroke="transparent" src={iconName} css={{ width: 18 }} mr="xs" />
          )}
          <H4 fontWeight="semiBold" color={!selectedVal ? 'secondary' : 'primaryText'}>
            {name || selectedVal || placeholder}
          </H4>
        </FlexLayout>
        <Icon src={fold ? 'unfoldMoreRounded' : 'unfoldLessRounded'} stroke="transparent" color="secondary" />
      </FlexLayout>
      <FlexLayout
        width="full"
        bg="white"
        border="regular"
        borderColor="border"
        borderTop="none"
        boxShadow="subtle"
        style={{ position: 'absolute', zIndex: 100, overflowY: 'hidden', display: fold ? 'none' : 'flex' }}
      >
        {dropdownItemsWithProps}
      </FlexLayout>
    </Box>
  );
};
